/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useCallback, useState } from 'react';
import { isObjEmptyOrNull, isStrEmptyOrNull, getErrorFromEndpoint } from '../UtilityFunctions';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

const useGetImagingDoc = () => {
  const { token } = useAuthContext();
  const { get } = useAbortController();
  const [imagingDoc, setImagingDoc] = useState<Blob | null>(null);
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);

  const imagingDocAPI = useCallback(
    async (docId: string): Promise<Blob | null> => {
      if (!token || isStrEmptyOrNull(docId)) return;
      const urlPath = process.env.REACT_APP_GET_IMAGING_DOC + docId;
      const request = { url: urlPath, responseType: 'blob' as 'blob' };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = get();

      const doc = await axiosCall(request, config, headers)
        .then(async (res) => {
          if (!isObjEmptyOrNull(res)) {
            const err = getErrorFromEndpoint(res);
            setErrors(err);
            if (isStrEmptyOrNull(err)) {
              const pdfBlob = new Blob([res], { type: 'application/pdf' });
              return pdfBlob;
            } else {
              return null;
            }
          }
        })
        .catch((e: Error) => {
          console.log('Error using imagingDocAPI in the useGetImagingDocument hook: ', e);
          setErrors(e.message);
          return null;
        });
      setImagingDoc(doc);
      return doc;
    },
    [get, token],
  );

  const getImagingDoc = useCallback(
    async (docId: string): Promise<Blob | null> => {
      setLoading(true);
      setImagingDoc(null);
      if (!isStrEmptyOrNull(docId)) {
        const doc = await imagingDocAPI(docId).finally(() => {
          setLoading(false);
        });
        return doc;
      }
      return null;
    },
    [imagingDocAPI],
  );

  return { imagingDoc, loading, errors, getImagingDoc };
};

export default useGetImagingDoc;
